import { apiErrorMessage } from "@/lib/apiErrorMessage";
import i18n from "@/i18n";
import { useCallback, useEffect, useState } from "react";
import { useLatest } from "@/hooks/useLatest";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft, Save, Pause, Play, Trash2, KeyRound, Ban, Check,
    Pencil, Building2, Store, Users as UsersIcon, ShieldCheck,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import CompanyLegalStatusPanel from "@/pages/admin/CompanyLegalStatusPanel";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * AdminCompanyDetail — Super Admin only.
 *
 * Manages every editable field of a customer company, plus its
 * restaurants, users, and subscription. Read-only for other roles is
 * enforced by the backend `require_super_admin` dependency.
 */
export default function AdminCompanyDetail() {
    const { companyId } = useParams();
    const navigate = useNavigate();
    const [company, setCompany] = useState(null);
    const [tab, setTab] = useState("overview");
    const [tempPassword, setTempPassword] = useState(null);

    const load = useCallback(() =>
        api.get(`/admin/companies`).then(({ data }) => {
            const c = data.find((x) => x.id === companyId);
            setCompany(c || null);
        }).catch((err) =>
            toast.error(apiErrorMessage(err))), [companyId]);
    useEffect(() => { load(); }, [load]);

    if (!company) return (
        <div className="p-6 text-sm text-neutral-500" data-testid="admin-company-detail-loading">
            {i18n.t("admin.l.Loading", "Loading…")}
        </div>
    );

    const isActive = company.status === "active";
    const toggleSuspend = async () => {
        const next = isActive ? "suspended" : "active";
        try {
            await api.patch(`/admin/companies/${companyId}`, { status: next });
            toast.success(next === "active" ? i18n.t("admin.companyActive", "Company active") : i18n.t("admin.companySuspended", "Company suspended"));
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-company-detail">
            <button onClick={() => navigate("/admin")}
                    data-testid="company-detail-back"
                    className="text-sm text-neutral-600 inline-flex items-center gap-1 hover:text-neutral-900">
                <ArrowLeft className="h-4 w-4" /> {i18n.t("admin.l.Back_to_companies", "Back to companies")}
            </button>

            <PageHeader
                title={company.name || i18n.t("admin.l.Untitled_company", "Untitled company")}
                description={
                    <span className="inline-flex items-center gap-2 text-xs">
                        <StatusPill status={company.status} />
                        <span className="text-neutral-500">·  ID {company.id.slice(0, 8)}</span>
                    </span>
                }
                action={
                    <button onClick={toggleSuspend}
                            data-testid="company-detail-suspend-toggle"
                            className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white text-sm font-semibold hover:bg-neutral-50">
                        {isActive
                            ? (<><Pause className="h-4 w-4" /> {i18n.t("admin.l.Suspend", "Suspend")}</>)
                            : (<><Play className="h-4 w-4" /> {i18n.t("admin.l.Reactivate", "Reactivate")}</>)}
                    </button>
                }
            />

            <div className="flex gap-1 border-b border-neutral-200">
                {[
                    { k: "overview", get label() { return i18n.t("admin.l.Overview", "Overview"); }, icon: Building2 },
                    { k: "restaurants", get label() { return i18n.t("admin.l.Restaurants", "Restaurants"); }, icon: Store },
                    { k: "users", get label() { return i18n.t("admin.l.Users", "Users"); }, icon: UsersIcon },
                    { k: "legal", get label() { return i18n.t("admin.l.Legal", "Legal"); }, icon: ShieldCheck },
                ].map(({ k, label, icon: Icon }) => (
                    <button key={k} onClick={() => setTab(k)}
                            data-testid={`company-detail-tab-${k}`}
                            className={`h-10 px-4 inline-flex items-center gap-2 text-sm font-semibold border-b-2 -mb-px ${
                                tab === k
                                    ? "border-neutral-900 text-neutral-900"
                                    : "border-transparent text-neutral-500 hover:text-neutral-700"
                            }`}>
                        <Icon className="h-4 w-4" /> {label}
                    </button>
                ))}
            </div>

            {tab === "overview" && <OverviewTab company={company} onSaved={load} />}
            {tab === "restaurants" && <RestaurantsTab companyId={companyId} />}
            {tab === "users" && (
                <UsersTab
                    companyId={companyId}
                    onTempPassword={(t) => setTempPassword(t)}
                />
            )}
            {tab === "legal" && <CompanyLegalStatusPanel companyId={companyId} />}

            <Dialog open={!!tempPassword} onOpenChange={(o) => { if (!o) setTempPassword(null); }}>
                <DialogContent className="max-w-md" data-testid="company-user-temp-password">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("admin.l.Temporary_password", "Temporary password")}</DialogTitle>
                        <DialogDescription>
                            Share with <span className="font-mono">{tempPassword?.email}</span> — shown once.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md bg-neutral-900 text-white p-3 text-center font-mono text-lg tracking-wide"
                         data-testid="company-user-temp-password-value">
                        {tempPassword?.password}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatusPill({ status }) {
    const cls = status === "active"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : status === "suspended"
            ? "bg-amber-50 text-amber-700 border-amber-200"
            : "bg-neutral-100 text-neutral-600 border-neutral-200";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold capitalize ${cls}`}>
            {status || "—"}
        </span>
    );
}

// -------------------- Overview --------------------
function OverviewTab({ company, onSaved }) {
    const [form, setForm] = useState(company);
    const [busy, setBusy] = useState(false);
    const companyRef = useLatest(company);
    useEffect(() => { setForm(companyRef.current); }, [company.id, companyRef]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const save = async () => {
        setBusy(true);
        try {
            const patch = {
                name: form.name, business_id: form.business_id,
                country: form.country, address: form.address,
                email: form.email, billing_email: form.billing_email,
                phone: form.phone, default_currency: form.default_currency,
                default_timezone: form.default_timezone,
                notes: form.notes,
                lifecycle_status: form.lifecycle_status,
            };
            await api.patch(`/admin/companies/${company.id}`, patch);
            toast.success(i18n.t("admin.l.Company_updated", "Company updated"));
            onSaved();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally { setBusy(false); }
    };

    const fields = [
        ["name", i18n.t("admin.l.Company_name", "Company name")],
        ["business_id", i18n.t("admin.l.Business_ID", "Business ID")],
        ["country", i18n.t("admin.l.Country", "Country")],
        ["address", i18n.t("admin.l.Legal_address", "Legal address")],
        ["email", i18n.t("admin.l.Contact_email", "Contact email")],
        ["billing_email", i18n.t("admin.l.Billing_email", "Billing email")],
        ["phone", i18n.t("admin.l.Phone", "Phone")],
        ["default_currency", i18n.t("admin.l.Currency", "Currency")],
        ["default_timezone", i18n.t("admin.l.Timezone", "Timezone")],
    ];

    return (
        <div className="space-y-6" data-testid="company-detail-overview">
            <div className="rounded-xl border border-neutral-200 bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.map(([k, label]) => (
                    <div key={k}>
                        <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">
                            {label}
                        </label>
                        <input value={form[k] || ""} onChange={set(k)}
                               data-testid={`company-field-${k}`}
                               className="mt-1 w-full h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm" />
                    </div>
                ))}
                <div className="md:col-span-2">
                    <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">
                        Internal notes (Super Admin only)
                    </label>
                    <textarea value={form.notes || ""} onChange={set("notes")}
                              data-testid="company-field-notes"
                              rows={3}
                              className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 bg-white text-sm" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">
                        {i18n.t("admin.l.Lifecycle_status", "Lifecycle status")}
                    </label>
                    <input value={form.lifecycle_status || ""} onChange={set("lifecycle_status")}
                           data-testid="company-field-lifecycle_status"
                           placeholder="lead | pilot | trial | active_customer | …"
                           className="mt-1 w-full h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm" />
                </div>
            </div>
            <div>
                <button onClick={save} disabled={busy}
                        data-testid="company-detail-save"
                        className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
                    <Save className="h-4 w-4" /> {busy ? i18n.t("admin.l.Saving", "Saving…") : i18n.t("admin.l.Save_changes", "Save changes")}
                </button>
            </div>
        </div>
    );
}

// -------------------- Restaurants --------------------
function RestaurantsTab({ companyId }) {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = useCallback(() => api.get("/admin/restaurants",
        { params: { company_id: companyId } }).then(({ data }) => setRows(data)), [companyId]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid="company-detail-restaurants">
            <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-4 py-3">{i18n.t("admin.l.Name", "Name")}</th>
                            <th className="px-4 py-3">{i18n.t("admin.l.City", "City")}</th>
                            <th className="px-4 py-3">{i18n.t("admin.l.Phone", "Phone")}</th>
                            <th className="px-4 py-3">{i18n.t("admin.l.Operation", "Operation")}</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 && (
                            <tr><td colSpan={5} className="px-4 py-6 text-neutral-500 text-sm">{i18n.t("admin.l.No_restaurants_yet", "No restaurants yet.")}</td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.id} data-testid={`company-restaurant-${r.id}`}>
                                <td className="px-4 py-3 font-medium">{r.name}</td>
                                <td className="px-4 py-3 text-neutral-600">{r.city || "—"}</td>
                                <td className="px-4 py-3 text-neutral-600">{r.phone || "—"}</td>
                                <td className="px-4 py-3 text-neutral-600 capitalize">{(r.operation_type || "").replace("_", " ")}</td>
                                <td className="px-4 py-3 text-right">
                                    <button data-testid={`company-restaurant-edit-${r.id}`}
                                            onClick={() => setEditing(r)}
                                            className="h-8 w-8 grid place-items-center rounded hover:bg-neutral-100">
                                        <Pencil className="h-4 w-4 text-neutral-600" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <RestaurantDialog target={editing} onClose={() => setEditing(null)}
                              onSaved={() => { setEditing(null); load(); }} />
        </div>
    );
}

function RestaurantDialog({ target, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const targetRef = useLatest(target);
    useEffect(() => { setForm(targetRef.current || {}); }, [target?.id, targetRef]);
    if (!target) return null;
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const save = async () => {
        try {
            await api.patch(`/admin/restaurants/${target.id}`, {
                name: form.name, city: form.city, address: form.address,
                phone: form.phone, email: form.email,
                operation_type: form.operation_type,
            });
            toast.success(i18n.t("admin.l.Restaurant_updated", "Restaurant updated"));
            onSaved();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };
    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="company-restaurant-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.t("admin.l.Edit_restaurant", "Edit restaurant")}</DialogTitle>
                </DialogHeader>
                {[["name",i18n.t("admin.l.Name", "Name")],["city",i18n.t("admin.l.City", "City")],["address",i18n.t("admin.l.Address", "Address")],
                  ["phone",i18n.t("admin.l.Phone", "Phone")],["email",i18n.t("admin.l.Email", "Email")],["operation_type",i18n.t("admin.l.Operation_type", "Operation type")]].map(([k,label])=>(
                    <div key={k}>
                        <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">{label}</label>
                        <input value={form[k] || ""} onChange={set(k)}
                               data-testid={`company-restaurant-field-${k}`}
                               className="mt-1 w-full h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm" />
                    </div>
                ))}
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-300 bg-white text-sm font-semibold">{i18n.t("admin.l.Cancel", "Cancel")}</button>
                    <button onClick={save} data-testid="company-restaurant-save"
                            className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold">
                        Save
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// -------------------- Users --------------------
function UsersTab({ companyId, onTempPassword }) {
    const [rows, setRows] = useState([]);
    const [rests, setRests] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = useCallback(() => api.get("/admin/users",
        { params: { company_id: companyId } }).then(({ data }) => setRows(data)), [companyId]);
    useEffect(() => {
        load();
        api.get("/admin/restaurants", { params: { company_id: companyId } })
           .then(({ data }) => setRests(data));
    }, [companyId, load]);

    const disable = async (u) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { active: false });
            toast.success(i18n.t("admin.l.User_disabled", "User disabled")); load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };
    const enable = async (u) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { active: true });
            toast.success(i18n.t("admin.l.User_reactivated", "User reactivated")); load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };
    const resetPw = async (u) => {
        if (!window.confirm(i18n.t("admin.confirmReset", { defaultValue: "Reset password for {{email}}?", email: u.email, interpolation: { escapeValue: false } }))) return;
        try {
            const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
            onTempPassword({ email: u.email, password: data.temporary_password });
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };
    const del = async (u) => {
        if (!window.confirm(i18n.t("admin.confirmDelete", { defaultValue: "Delete {{email}}? This cannot be undone.", email: u.email, interpolation: { escapeValue: false } }))) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success(i18n.t("admin.l.User_deleted", "User deleted")); load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    return (
        <div className="space-y-4" data-testid="company-detail-users">
            <div className="flex justify-end">
                <button onClick={() => setEditing({ __new: true, role: "restaurant_admin" })}
                        data-testid="company-user-new"
                        className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-neutral-900 text-white text-xs font-semibold">
                    + New customer user
                </button>
            </div>
            <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-4 py-3">{i18n.t("admin.l.Name", "Name")}</th>
                            <th className="px-4 py-3">{i18n.t("admin.l.Email", "Email")}</th>
                            <th className="px-4 py-3">{i18n.t("admin.l.Role", "Role")}</th>
                            <th className="px-4 py-3">{i18n.t("admin.l.Restaurant", "Restaurant")}</th>
                            <th className="px-4 py-3">{i18n.t("admin.l.Status", "Status")}</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-6 text-neutral-500 text-sm">{i18n.t("admin.l.No_users_yet", "No users yet.")}</td></tr>
                        )}
                        {rows.map((u) => {
                            const isActive = u.active !== false && !u.locked_at;
                            const restName = rests.find((r) => r.id === u.restaurant_id)?.name || "—";
                            return (
                                <tr key={u.id} data-testid={`company-user-${u.id}`}>
                                    <td className="px-4 py-3 font-medium">{u.name}</td>
                                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                                    <td className="px-4 py-3 capitalize">{(u.profile_role || u.role || "").replace("_", " ")}</td>
                                    <td className="px-4 py-3 text-neutral-600">{restName}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full border text-[11px] font-semibold ${isActive
                                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                            : "bg-neutral-100 text-neutral-500 border-neutral-200"}`}>
                                            {isActive ? i18n.t("admin.l.Active", "Active") : i18n.t("admin.l.Disabled", "Disabled")}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap space-x-1">
                                        <button onClick={() => setEditing(u)}
                                                data-testid={`company-user-edit-${u.id}`}
                                                className="h-8 w-8 grid place-items-center rounded hover:bg-neutral-100">
                                            <Pencil className="h-4 w-4 text-neutral-600" />
                                        </button>
                                        <button onClick={() => resetPw(u)}
                                                data-testid={`company-user-reset-${u.id}`}
                                                title={i18n.t("admin.l.Reset_password", "Reset password")}
                                                className="h-8 w-8 grid place-items-center rounded hover:bg-neutral-100">
                                            <KeyRound className="h-4 w-4 text-neutral-600" />
                                        </button>
                                        {isActive ? (
                                            <button onClick={() => disable(u)}
                                                    data-testid={`company-user-disable-${u.id}`}
                                                    className="h-8 w-8 grid place-items-center rounded hover:bg-amber-50">
                                                <Ban className="h-4 w-4 text-amber-700" />
                                            </button>
                                        ) : (
                                            <button onClick={() => enable(u)}
                                                    data-testid={`company-user-enable-${u.id}`}
                                                    className="h-8 w-8 grid place-items-center rounded hover:bg-emerald-50">
                                                <Check className="h-4 w-4 text-emerald-700" />
                                            </button>
                                        )}
                                        <button onClick={() => del(u)}
                                                data-testid={`company-user-delete-${u.id}`}
                                                className="h-8 w-8 grid place-items-center rounded hover:bg-red-50">
                                            <Trash2 className="h-4 w-4 text-red-600" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <UserDialog companyId={companyId} target={editing} rests={rests}
                        onClose={() => setEditing(null)}
                        onSaved={(temp) => {
                            setEditing(null);
                            load();
                            if (temp) onTempPassword(temp);
                        }} />
        </div>
    );
}

const CUSTOMER_ROLES = [
    { value: "restaurant_admin", get label() { return i18n.t("admin.l.Restaurant_Admin", "Restaurant Admin"); } },
    { value: "head_chef", get label() { return i18n.t("admin.l.Head_Chef", "Head Chef"); } },
    { value: "kitchen_staff", get label() { return i18n.t("admin.l.Kitchen_Staff", "Kitchen Staff"); } },
    { value: "corporate", get label() { return i18n.t("admin.l.Corporate", "Corporate"); } },
    { value: "read_only", get label() { return i18n.t("admin.l.Read_only", "Read only"); } },
];

function UserDialog({ companyId, target, rests, onClose, onSaved }) {
    const isNew = target?.__new === true;
    const [form, setForm] = useState({
        name: "", email: "", phone: "", role: "restaurant_admin",
        restaurant_id: "", active: true,
    });
    const targetRef = useLatest(target);
    useEffect(() => {
        const t = targetRef.current;
        if (t) {
            setForm({
                name: t.name || "", email: t.email || "",
                phone: t.phone || "",
                role: t.profile_role || t.role || "restaurant_admin",
                restaurant_id: t.restaurant_id || "",
                active: t.active !== false,
            });
        }
    }, [target?.id, target?.__new, targetRef]);
    if (!target) return null;
    const set = (k) => (e) =>
        setForm({ ...form, [k]: e.target.value });
    const needsRestaurant = ["restaurant_admin", "head_chef", "kitchen_staff", "restaurant", "read_only"]
        .includes(form.role);
    const save = async () => {
        try {
            if (isNew) {
                if (!form.name || !form.email) {
                    toast.error(i18n.t("admin.l.Name_and_email_are_required", "Name and email are required")); return;
                }
                if (needsRestaurant && !form.restaurant_id) {
                    toast.error(i18n.t("admin.l.Choose_a_restaurant", "Choose a restaurant")); return;
                }
                const body = {
                    name: form.name.trim(), email: form.email.trim().toLowerCase(),
                    phone: form.phone.trim() || null,
                    role: form.role,
                    company_id: companyId,
                    restaurant_id: needsRestaurant ? form.restaurant_id : null,
                    status: form.active ? "active" : "suspended",
                };
                const { data } = await api.post("/admin/users", body);
                toast.success(i18n.t("admin.l.User_created", "User created"));
                onSaved(data.temporary_password
                    ? { email: body.email, password: data.temporary_password } : null);
            } else {
                await api.patch(`/admin/users/${target.id}`, {
                    name: form.name.trim(),
                    email: form.email.trim().toLowerCase(),
                    phone: form.phone.trim() || null,
                    role: form.role,
                    restaurant_id: needsRestaurant ? form.restaurant_id : null,
                    active: form.active,
                });
                toast.success(i18n.t("admin.l.User_updated", "User updated"));
                onSaved(null);
            }
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };
    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="company-user-dialog">
                <DialogHeader>
                    <DialogTitle>{isNew ? i18n.t("admin.l.New_customer_user", "New customer user") : i18n.t("admin.l.Edit_customer_user", "Edit customer user")}</DialogTitle>
                </DialogHeader>
                {[["name",i18n.t("admin.l.Name", "Name")],["email",i18n.t("admin.l.Email", "Email")],["phone",i18n.t("admin.l.Phone", "Phone")]].map(([k,label])=>(
                    <div key={k}>
                        <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">{label}</label>
                        <input value={form[k]} onChange={set(k)}
                               data-testid={`company-user-field-${k}`}
                               className="mt-1 w-full h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm" />
                    </div>
                ))}
                <div>
                    <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">{i18n.t("admin.l.Role", "Role")}</label>
                    <select value={form.role} onChange={set("role")}
                            data-testid="company-user-field-role"
                            className="mt-1 w-full h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm">
                        {CUSTOMER_ROLES.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                    </select>
                </div>
                {needsRestaurant && (
                    <div>
                        <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">{i18n.t("admin.l.Restaurant", "Restaurant")}</label>
                        <select value={form.restaurant_id} onChange={set("restaurant_id")}
                                data-testid="company-user-field-restaurant"
                                className="mt-1 w-full h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm">
                            <option value="">{i18n.t("admin.l.Select", "— Select —")}</option>
                            {rests.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </div>
                )}
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                    <input type="checkbox" checked={form.active}
                           data-testid="company-user-field-active"
                           onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                    {i18n.t("admin.l.Active", "Active")}
                </label>
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-300 bg-white text-sm font-semibold">{i18n.t("admin.l.Cancel", "Cancel")}</button>
                    <button onClick={save} data-testid="company-user-save"
                            className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold">
                        {isNew ? i18n.t("admin.l.Create_user", "Create user") : i18n.t("admin.l.Save_changes", "Save changes")}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
